import React, { useState, useEffect } from 'react';

const mockOrders = [
  {
    id: 'OTG-9021',
    type: 'ride',
    pickup: 'Westlands Roundabout',
    dropoff: 'JKIA Terminal 1A',
    fare: 850,
    distance: '14.2 km',
    eta: '6 min away',
    priority: 'high',
    passengerName: 'Amina K.',
    passengerRating: 4.7,
    surgeMultiplier: 1.4,
  },
  {
    id: 'OTG-9022',
    type: 'delivery',
    pickup: 'Carrefour Sarit Centre',
    dropoff: 'Lavington Green Apts',
    fare: 320,
    distance: '5.8 km',
    eta: '3 min away',
    priority: 'normal',
    passengerName: 'Brian O.',
    passengerRating: 4.9,
    surgeMultiplier: null,
  },
];

const TIMER_DURATION = 25;

const OrderCard = ({ order, onAccept, onDecline }) => {
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);

  useEffect(() => {
    if (timeLeft <= 0) {
      onDecline(order?.id);
      return;
    }
    const t = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, order?.id, onDecline]);

  const progress = (timeLeft / TIMER_DURATION) * 100;
  const isUrgent = timeLeft <= 8;

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
      order?.priority === 'high' ? 'border-[#ffd700]/40 bg-[#0a1628]' : 'border-white/10 bg-[#0f2040]'
    }`}>
      {/* Priority badge + timer bar */}
      <div className="relative h-1.5 bg-white/10">
        <div
          className={`h-full transition-all duration-1000 ${isUrgent ? 'bg-red-500' : 'bg-[#ffd700]'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
              order?.type === 'ride' ? 'bg-blue-500/20' : 'bg-purple-500/20'
            }`}>
              {order?.type === 'ride' ? (
                <svg viewBox="0 0 24 24" fill={order?.type === 'ride' ? '#60a5fa' : '#a78bfa'} className="w-4 h-4">
                  <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="#a78bfa" className="w-4 h-4">
                  <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                </svg>
              )}
            </div>
            <div>
              <span className="text-white/50 text-xs capitalize">{order?.type}</span>
              {order?.priority === 'high' && (
                <span className="ml-2 text-[10px] font-bold text-[#ffd700] bg-[#ffd700]/10 px-1.5 py-0.5 rounded-full">PRIORITY</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1">
              <span className="text-[#ffd700] font-black text-lg">KSh {order?.fare}</span>
              {order?.surgeMultiplier && (
                <span className="text-[10px] font-bold text-orange-400 bg-orange-400/10 px-1.5 py-0.5 rounded-full">{order?.surgeMultiplier}x</span>
              )}
            </div>
            <span className="text-white/40 text-xs">{order?.distance}</span>
          </div>
        </div>

        {/* Route */}
        <div className="space-y-2 mb-3">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
            </div>
            <div>
              <p className="text-white/40 text-[10px] uppercase tracking-wider">Pickup</p>
              <p className="text-white text-sm font-medium leading-tight">{order?.pickup}</p>
            </div>
          </div>
          <div className="ml-2.5 w-px h-3 bg-white/10" />
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-[#ffd700]/20 border border-[#ffd700]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#ffd700]" />
            </div>
            <div>
              <p className="text-white/40 text-[10px] uppercase tracking-wider">Drop-off</p>
              <p className="text-white text-sm font-medium leading-tight">{order?.dropoff}</p>
            </div>
          </div>
        </div>

        {/* Passenger + ETA */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#ffd700]/20 flex items-center justify-center">
              <span className="text-[10px] font-bold text-[#ffd700]">{order?.passengerName?.[0]}</span>
            </div>
            <span className="text-white/60 text-xs">{order?.passengerName}</span>
            <div className="flex items-center gap-0.5">
              <svg viewBox="0 0 24 24" fill="#ffd700" className="w-3 h-3">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
              <span className="text-white/50 text-xs">{order?.passengerRating}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-white/5 rounded-full px-2.5 py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/60 text-xs">{order?.eta}</span>
          </div>
        </div>

        {/* Accept / Decline */}
        <div className="flex gap-2">
          <button
            onClick={() => onDecline(order?.id)}
            className="btn-secondary flex-1 hover:border-red-500/40 hover:text-red-500 hover:bg-red-50"
          >
            Decline
          </button>
          <button
            onClick={() => onAccept(order)}
            className="btn-primary flex-[2]"
          >
            <svg viewBox="0 0 24 24" fill="#0a1628" className="w-4 h-4">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            Accept · {timeLeft}s
          </button>
        </div>
      </div>
    </div>
  );
};

const ActiveOrderQueue = ({ isOnline, onAcceptOrder }) => {
  const [orders, setOrders] = useState(mockOrders);

  const handleDecline = (id) => {
    setOrders(prev => prev?.filter(o => o?.id !== id));
  };

  const handleAccept = (order) => {
    setOrders(prev => prev?.filter(o => o?.id !== order?.id));
    onAcceptOrder?.(order);
  };

  if (!isOnline) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-3">
          <svg viewBox="0 0 24 24" fill="rgba(255,255,255,0.2)" className="w-8 h-8">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
          </svg>
        </div>
        <p className="text-white/30 text-sm font-medium">Go online to receive orders</p>
      </div>
    );
  }

  if (orders?.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-2xl bg-[#ffd700]/10 flex items-center justify-center mx-auto mb-3">
          <svg viewBox="0 0 24 24" fill="#ffd700" className="w-8 h-8">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
          </svg>
        </div>
        <p className="text-white/50 text-sm font-medium">Waiting for orders...</p>
        <p className="text-white/25 text-xs mt-1">New requests will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders?.map(order => (
        <OrderCard
          key={order?.id}
          order={order}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      ))}
    </div>
  );
};

export default ActiveOrderQueue;
